<template>
  <main class="app-main prices-offer-form">
    <div class="app-content-header">
      <div class="container-fluid d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h3 class="mb-1">{{ isEdit ? 'تعديل عرض السعر' : 'إضافة عرض سعر عقاري جديد' }}</h3>
          <div v-if="isEdit && form.offerNumber" class="text-muted small" dir="ltr">{{ form.offerNumber }}</div>
        </div>
        <router-link to="/pricesOffers" class="btn btn-outline-secondary">العودة إلى عروض الأسعار</router-link>
      </div>
    </div>

    <div class="app-content">
      <div class="container-fluid">
        <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
        <mainLoader v-if="loading" />
        <form v-else class="offer-card card shadow-sm" @submit.prevent="submitOffer">
          <div class="card-header offer-card-header d-flex align-items-center justify-content-between">
            <strong><i class="fa-solid fa-file-signature me-2"></i>{{ isEdit ? 'تعديل بيانات العرض' : 'بيانات عرض السعر' }}</strong>
            <span v-if="isEdit" class="badge bg-primary" dir="ltr">{{ form.offerNumber }}</span>
          </div>
          <div class="card-body">
            <div v-if="!isEdit && !company.companyName" class="alert alert-warning">
              أضف اسم الشركة في <router-link to="/companyDetails">بيانات الشركة</router-link> قبل إنشاء العرض.
            </div>
            <div v-if="!branches.length && !selectedOldBranch" class="alert alert-warning">
              أضف فرعاً في <router-link to="/companyDetails">بيانات الشركة</router-link> قبل حفظ العرض.
            </div>
            <div v-if="!bankAccounts.length" class="alert alert-warning">
              أضف حساباً بنكياً في <router-link to="/companyDetails">بيانات الشركة</router-link> ليظهر في بيانات التحويل.
            </div>

            <section class="offer-section">
              <h5><span class="section-number">1</span> البيانات الأساسية والإدارية</h5>
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <label for="offer-branch" class="form-label">الفرع <span class="req">*</span></label>
                  <select id="offer-branch" v-model="form.branchId" class="form-select" required>
                    <option value="">اختر الفرع</option>
                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
                    <option v-if="selectedOldBranch" :value="selectedOldBranch.id">{{ selectedOldBranch.name }}</option>
                  </select>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-employee" class="form-label">الموظف المسؤول <span class="req">*</span></label>
                  <input id="offer-employee" v-model.trim="form.employeeName" class="form-control" required maxlength="255" placeholder="اسم الموظف المسؤول" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-date" class="form-label">تاريخ العرض <span class="req">*</span></label>
                  <input id="offer-date" v-model="form.offerDate" class="form-control" type="date" required />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-duration" class="form-label">مدة العرض (أيام) <span class="req">*</span></label>
                  <input id="offer-duration" v-model.number="form.durationDays" class="form-control" type="number" min="1" max="365" required />
                  <div v-if="expiryDate" class="form-text">ينتهي العرض في {{ expiryDate }}</div>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-grace-period" class="form-label">فترة السماح (أيام)</label>
                  <input id="offer-grace-period" v-model.number="form.gracePeriodDays" class="form-control" type="number" min="0" max="365" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-purpose" class="form-label">الغرض من العرض <span class="req">*</span></label>
                  <select id="offer-purpose" v-model="form.purpose" class="form-select" required>
                    <option value="sale">بيع</option>
                    <option value="rent">تأجير</option>
                  </select>
                </div>
              </div>
            </section>

            <section class="offer-section">
              <h5><span class="section-number">2</span> بيانات العميل والمستفيد</h5>
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <label for="offer-client" class="form-label">اسم العميل الكامل <span class="req">*</span></label>
                  <input id="offer-client" v-model.trim="form.clientName" class="form-control" maxlength="255" required placeholder="اسم العميل الكامل" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-mobile" class="form-label">رقم الجوال <span class="req">*</span></label>
                  <input id="offer-mobile" v-model.trim="form.clientMobile" class="form-control" type="tel" dir="ltr" maxlength="30" required placeholder="05xxxxxxxx" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-email" class="form-label">البريد الإلكتروني</label>
                  <input id="offer-email" v-model.trim="form.clientEmail" class="form-control" type="email" dir="ltr" maxlength="255" placeholder="client@example.com" />
                </div>
              </div>
            </section>

            <section class="offer-section">
              <h5><span class="section-number">3</span> بيانات العقار، رقم الصك، والوصف المختصر</h5>
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <label for="offer-property" class="form-label">اسم العقار / المشروع <span class="req">*</span></label>
                  <input id="offer-property" v-model.trim="form.propertyName" class="form-control" maxlength="255" required placeholder="مثال: مشروع أماكن فلل" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-unit" class="form-label">رقم العقار / الوحدة <span class="req">*</span></label>
                  <input id="offer-unit" v-model.trim="form.unitNumber" class="form-control" maxlength="100" required placeholder="وحدة 101 / شقة 45" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-deed-number" class="form-label">رقم الصك العقاري <span class="req">*</span></label>
                  <input id="offer-deed-number" v-model.trim="form.deedNumber" class="form-control" maxlength="100" :required="!isEdit || Boolean(form.deedNumber)" placeholder="رقم الصك العقاري" />
                </div>
                <div class="col-12 col-md-3">
                  <label for="offer-area" class="form-label">المساحة (م²) <span class="req">*</span></label>
                  <input id="offer-area" v-model.number="form.areaSqm" class="form-control" type="number" min="0.01" step="0.01" required @input="syncBasePrice" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-meter-price" class="form-label">سعر المتر (ر.س)</label>
                  <input id="offer-meter-price" v-model.number="form.pricePerSqm" class="form-control" type="number" min="0.01" step="0.01" :required="!form.manualBasePrice" @input="syncBasePrice" />
                </div>
                <div class="col-12 col-md-2 d-flex align-items-end">
                  <div class="form-check mb-2">
                    <input id="manual-base-price" v-model="form.manualBasePrice" class="form-check-input" type="checkbox" @change="syncBasePrice" />
                    <label for="manual-base-price" class="form-check-label">إدخال السعر الأساسي يدوياً</label>
                  </div>
                </div>
                <div class="col-12 col-md-3">
                  <label for="offer-base-price" class="form-label">السعر الأساسي قبل الخصم (ر.س) <span class="req">*</span></label>
                  <input id="offer-base-price" v-model.number="form.basePrice" class="form-control" type="number" min="0.01" step="0.01" required :readonly="!form.manualBasePrice" />
                </div>
                <div class="col-12">
                  <label for="offer-property-description" class="form-label">وصف مختصر للعقار</label>
                  <textarea id="offer-property-description" v-model.trim="form.propertyDescription" class="form-control" rows="2" maxlength="1000" placeholder="وصف مختصر يُعرض في عرض السعر"></textarea>
                </div>
              </div>
            </section>

            <section class="offer-section offer-section-box">
              <h5><span class="section-number">4</span> احتساب المبلغ، الخصم، السعي والضريبة (15%)</h5>
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <label class="form-label">السعر الأساسي قبل الخصم (ر.س)</label>
                  <div class="form-control calculated-value">{{ money(totals.basePrice) }} ر.س</div>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-discount" class="form-label">مبلغ الخصم (ر.س)</label>
                  <input id="offer-discount" v-model.number="form.discountAmount" class="form-control" :class="{ 'is-invalid': discountInvalid }" type="number" min="0" :max="totals.basePrice" step="0.01" />
                  <div v-if="discountInvalid" class="invalid-feedback">مبلغ الخصم يجب ألا يتجاوز السعر الأساسي.</div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label">السعر بعد الخصم (ر.س)</label>
                  <div class="form-control calculated-value">{{ money(totals.discountedBasePrice) }} ر.س</div>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-commission-method" class="form-label">طريقة احتساب السعي</label>
                  <select id="offer-commission-method" v-model="form.commissionMethod" class="form-select">
                    <option value="percent">نسبة مئوية (%)</option>
                    <option value="fixed">مبلغ ثابت (ر.س)</option>
                  </select>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-commission-rate" class="form-label">{{ form.commissionMethod === 'fixed' ? 'مبلغ السعي (ر.س)' : 'نسبة السعي (%)' }}</label>
                  <input id="offer-commission-rate" v-model.number="form.commissionRate" class="form-control" type="number" min="0" :max="form.commissionMethod === 'percent' ? 100 : undefined" step="0.01" />
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label">مبلغ السعي الناتج</label>
                  <div class="form-control calculated-value">{{ money(totals.commissionAmount) }} ر.س</div>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-water-fee" class="form-label">رسوم المياه (ر.س)</label>
                  <input id="offer-water-fee" v-model.number="form.waterFee" class="form-control" type="number" min="0" step="0.01" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-contract-fee" class="form-label">رسوم العقد الإلكتروني (ر.س)</label>
                  <input id="offer-contract-fee" v-model.number="form.contractFee" class="form-control" type="number" min="0" step="0.01" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-insurance-fee" class="form-label">رسوم التأمين (ر.س)</label>
                  <input id="offer-insurance-fee" v-model.number="form.insuranceFee" class="form-control" type="number" min="0" step="0.01" />
                </div>
              </div>
              <div class="offer-summary mt-3">
                <div><small>السعر قبل الخصم</small><strong>{{ money(totals.basePrice) }} ر.س</strong></div>
                <div><small>ضريبة القيمة المضافة (15%)</small><strong>{{ money(totals.vatAmount) }} ر.س</strong></div>
                <div><small>إجمالي السعي والرسوم</small><strong>{{ money(feesTotal) }} ر.س</strong></div>
                <div><small>المبلغ الإجمالي النهائي المطلوب</small><strong>{{ money(totals.totalAmount) }} ر.س</strong></div>
              </div>
            </section>

            <section class="offer-section offer-section-payments">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h5 class="mb-0"><span class="section-number">5</span> جدول الدفعات المالية (إن وجد)</h5>
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="form.installments.length >= 24" @click="addInstallment">
                  <i class="bi bi-plus-lg me-1" aria-hidden="true"></i> إضافة دفعة
                </button>
              </div>
              <p class="form-text mb-3">يمكنك تقسيم إجمالي العرض إلى دفعات ميسرة أو إبقائه فارغاً ليتم حسابه كدفعة واحدة.</p>
              <div v-if="form.installments.length" class="table-responsive installment-table-wrap">
                <table class="table align-middle installment-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">اسم / وصف الدفعة</th>
                      <th scope="col">النسبة (%)</th>
                      <th scope="col">المبلغ (ر.س)</th>
                      <th scope="col">تاريخ / شرط الاستحقاق</th>
                      <th scope="col" class="text-center">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(installment, index) in form.installments" :key="installmentKeys[index]">
                      <td><input v-model.trim="installment.label" class="form-control form-control-sm" :aria-label="`وصف الدفعة ${index + 1}`" maxlength="255" required placeholder="الدفعة الأولى" /></td>
                      <td><input v-model.number="installment.percentage" class="form-control form-control-sm" :aria-label="`نسبة الدفعة ${index + 1}`" type="number" min="0" max="100" step="any" required @change="updateInstallment(index, 'percentage')" /></td>
                      <td><input v-model.number="installment.amount" class="form-control form-control-sm" :aria-label="`مبلغ الدفعة ${index + 1}`" type="number" min="0" :max="totals.totalAmount" step="0.01" required @change="updateInstallment(index, 'amount')" /></td>
                      <td><input v-model.trim="installment.dueCondition" class="form-control form-control-sm" :aria-label="`استحقاق الدفعة ${index + 1}`" maxlength="255" required placeholder="عند توقيع العقد أو تاريخ محدد" /></td>
                      <td class="text-center"><button type="button" class="btn btn-sm btn-outline-danger" :aria-label="`حذف الدفعة ${index + 1}`" @click="removeInstallment(index)"><i class="bi bi-trash" aria-hidden="true"></i></button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="installment-empty">لم تُضف دفعات مالية بعد.</div>
              <div v-if="form.installments.length" class="installment-summary mt-3">
                <span>إجمالي الدفعات: <strong>{{ money(installmentsTotal) }} ر.س</strong></span>
                <span>المتبقي من إجمالي العرض: <strong>{{ money(installmentsRemaining) }} ر.س</strong></span>
              </div>
            </section>

            <section class="offer-section offer-section-bank">
              <h5><span class="section-number">6</span> بيانات الحساب البنكي وإرسال العربون</h5>
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <label for="offer-bank-choice" class="form-label">حساب الشركة البنكي</label>
                  <select id="offer-bank-choice" v-model="form.bankAccountId" class="form-select" @change="chooseBankAccount">
                    <option value="">اختر حساباً محفوظاً</option>
                    <option v-for="account in bankAccounts" :key="account.id" :value="account.id">{{ account.label || account.bankName }} — {{ account.iban }}</option>
                    <option v-if="selectedOldBank" :value="selectedOldBank.id">{{ selectedOldBank.name }} — {{ form.iban }}</option>
                  </select>
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-bank-name" class="form-label">اسم البنك <span class="req">*</span></label>
                  <input id="offer-bank-name" v-model.trim="form.bankName" class="form-control" required maxlength="255" @input="unlinkBankAccount" />
                </div>
                <div class="col-12 col-md-4">
                  <label for="offer-account-name" class="form-label">اسم الحساب البنكي <span class="req">*</span></label>
                  <input id="offer-account-name" v-model.trim="form.accountName" class="form-control" required maxlength="255" @input="unlinkBankAccount" />
                </div>
                <div class="col-12 col-md-6">
                  <label for="offer-iban" class="form-label">رقم الآيبان IBAN <span class="req">*</span></label>
                  <input id="offer-iban" v-model.trim="form.iban" class="form-control" dir="ltr" required maxlength="34" @input="unlinkBankAccount" />
                </div>
                <div class="col-12 col-md-6">
                  <label for="offer-account-number" class="form-label">رقم الحساب <span class="req">*</span></label>
                  <input id="offer-account-number" v-model.trim="form.accountNumber" class="form-control" dir="ltr" maxlength="100" required @input="unlinkBankAccount" />
                </div>
                <div class="col-12">
                  <label for="offer-payment-note" class="form-label">رسالة وتنويه دفع العربون (تظهر بالطباعة) <span class="req">*</span></label>
                  <textarea id="offer-payment-note" v-model.trim="form.paymentNote" class="form-control" rows="2" maxlength="1000" required></textarea>
                </div>
              </div>
            </section>
          </div>
          <div class="card-footer d-flex justify-content-end gap-2">
            <router-link to="/pricesOffers" class="btn btn-outline-secondary">إلغاء</router-link>
            <button type="submit" class="btn btn-success" :disabled="saving || (!branches.length && !selectedOldBranch) || (!isEdit && !company.companyName) || !canSave">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-save me-1" aria-hidden="true"></i>
              {{ isEdit ? 'حفظ التعديلات' : 'حفظ عرض السعر' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess, calculateOfferTotals, offerExpiryDate } from '@/helpers/tools';

const defaultPaymentNote = 'في حالة الموافقة والاعتماد، يرجى إرسال عربون الحجز على البيانات البنكية الموضحة أعلاه، مع إرسال صورة التحويل لتثبيت العرض.';
const toCents = value => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? Math.round((amount + Number.EPSILON) * 100) : 0;
};
const percentageFor = (amountCents, totalCents) => totalCents
  ? Math.round(amountCents * 1000000 / totalCents) / 10000
  : 0;
const submittedPercentageFor = (amountCents, totalCents) => totalCents
  ? Math.floor(amountCents / totalCents * 1000000 + 0.0000001) / 10000
  : 0;

const today = () => {
  const value = new Date();
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
};

const emptyOffer = () => ({
  offerNumber: '', branchId: '', branchName: '', employeeName: '', offerDate: today(),
  durationDays: 15, gracePeriodDays: 0, purpose: 'sale', clientName: '', clientMobile: '', clientEmail: '',
  propertyName: '', unitNumber: '', deedNumber: '', propertyDescription: '',
  areaSqm: 0, pricePerSqm: 0, basePrice: 0, discountAmount: 0,
  manualBasePrice: false, commissionMethod: 'percent', commissionRate: 2.5,
  waterFee: 0, contractFee: 0, insuranceFee: 0, installments: [], bankAccountId: '',
  bankName: '', accountName: '', iban: '', accountNumber: '', paymentNote: defaultPaymentNote,
});

export default {
  name: 'PricesOfferForm',
  data() {
    return {
      form: emptyOffer(),
      installmentBases: [], installmentKeys: [], nextInstallmentKey: 1,
      company: {}, branches: [], bankAccounts: [],
      loading: true, saving: false, hasError: false, message: '', errorNumber: 0,
    };
  },
  computed: {
    isEdit() { return Boolean(this.$route.params.id); },
    canSave() { return checkAccess(this.isEdit ? 'prices_offers_update' : 'prices_offers_create'); },
    expiryDate() { return offerExpiryDate(this.form.offerDate, this.form.durationDays); },
    totals() { return calculateOfferTotals(this.form); },
    discountInvalid() {
      const discount = Number(this.form.discountAmount);
      return !Number.isFinite(discount) || discount < 0 || toCents(discount) > toCents(this.totals.basePrice);
    },
    feesTotal() {
      return this.totals.commissionAmount + this.totals.waterFee + this.totals.contractFee + this.totals.insuranceFee;
    },
    installmentsTotal() {
      return this.form.installments.reduce((total, row) => total + toCents(row.amount), 0) / 100;
    },
    installmentsRemaining() {
      return Math.max(0, toCents(this.totals.totalAmount) - toCents(this.installmentsTotal)) / 100;
    },
    selectedOldBranch() {
      return this.form.branchId && !this.branches.some(branch => String(branch.id) === String(this.form.branchId))
        ? { id: this.form.branchId, name: this.form.branchName || 'الفرع المحفوظ' }
        : null;
    },
    selectedOldBank() {
      return this.form.bankAccountId && !this.bankAccounts.some(account => String(account.id) === String(this.form.bankAccountId))
        ? { id: this.form.bankAccountId, name: this.form.bankSnapshot?.label || this.form.bankName || 'الحساب البنكي المحفوظ' }
        : null;
    },
  },
  watch: {
    'totals.totalAmount'() {
      if (this.totals.basePrice > 0 && !this.discountInvalid && this.totals.totalAmount > 0)
        this.recalculateInstallments();
    },
  },
  methods: {
    money(value) {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
    },
    syncBasePrice() {
      if (!this.form.manualBasePrice) {
        this.form.basePrice = calculateOfferTotals(this.form).basePrice;
      }
    },
    addInstallment() {
      if (this.form.installments.length >= 24) return;
      this.form.installments.push({ label: '', percentage: 0, amount: 0, dueCondition: '' });
      this.installmentBases.push('percentage');
      this.installmentKeys.push(this.nextInstallmentKey++);
    },
    removeInstallment(index) {
      this.form.installments.splice(index, 1);
      this.installmentBases.splice(index, 1);
      this.installmentKeys.splice(index, 1);
    },
    updateInstallment(index, basis) {
      const row = this.form.installments[index];
      if (!row) return;
      const totalCents = toCents(this.totals.totalAmount);
      const otherCents = this.form.installments.reduce((sum, item, itemIndex) =>
        sum + (itemIndex === index ? 0 : toCents(item.amount)), 0);
      const availableCents = Math.max(0, totalCents - otherCents);
      const percentage = Math.min(100, Math.max(0, Number(row.percentage) || 0));
      const requestedCents = basis === 'percentage'
        ? Math.round(totalCents * percentage / 100)
        : toCents(row.amount);
      const amountCents = Math.min(availableCents, requestedCents);
      row.amount = amountCents / 100;
      row.percentage = basis === 'amount' || amountCents !== requestedCents
        ? percentageFor(amountCents, totalCents)
        : percentage;
      this.installmentBases[index] = basis;
    },
    recalculateInstallments() {
      const totalCents = toCents(this.totals.totalAmount);
      let remainingCents = totalCents;
      this.form.installments.forEach((row, index) => {
        const basis = this.installmentBases[index] || 'amount';
        const percentage = Math.min(100, Math.max(0, Number(row.percentage) || 0));
        const requestedCents = basis === 'percentage'
          ? Math.round(totalCents * percentage / 100)
          : toCents(row.amount);
        const amountCents = Math.min(remainingCents, requestedCents);
        row.amount = amountCents / 100;
        row.percentage = basis === 'amount' || amountCents !== requestedCents
          ? percentageFor(amountCents, totalCents)
          : percentage;
        remainingCents -= amountCents;
      });
    },
    chooseBankAccount() {
      const account = this.bankAccounts.find(item => String(item.id) === String(this.form.bankAccountId));
      if (!account) return;
      this.form.bankName = account.bankName || '';
      this.form.accountName = account.accountName || '';
      this.form.iban = account.iban || '';
      this.form.accountNumber = account.accountNumber || '';
    },
    unlinkBankAccount() {
      this.form.bankAccountId = '';
    },
    fail(message) {
      this.message = message;
      this.errorNumber = 0;
      this.hasError = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    requestError(error) {
      [this.message, this.errorNumber] = this.$globalError(error, true);
      this.hasError = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    async load() {
      this.loading = true;
      try {
        const requests = [this.$http.get('/api/admin/pricesOffers/options')];
        if (this.isEdit) requests.push(this.$http.get(`/api/admin/pricesOffers/retrieve/${this.$route.params.id}`));
        const [optionsResponse, offerResponse] = await Promise.all(requests);
        const options = optionsResponse.data;
        this.company = options.company || {};
        this.branches = Array.isArray(options.branches) ? options.branches : [];
        this.bankAccounts = Array.isArray(options.bankAccounts) ? options.bankAccounts : [];
        if (offerResponse) {
          const row = offerResponse.data.row;
          this.form = { ...emptyOffer(), ...row };
          this.form.installments = Array.isArray(row.installments)
            ? row.installments.map(item => ({
              label: item.label || '', percentage: Number(item.percentage) || 0,
              amount: Number(item.amount) || 0, dueCondition: item.dueCondition || '',
            }))
            : [];
          this.installmentBases = this.form.installments.map(item => item.amount > 0 ? 'amount' : 'percentage');
          this.installmentKeys = this.form.installments.map(() => this.nextInstallmentKey++);
          if (!this.form.bankName && row.bankSnapshot) {
            this.form.bankName = row.bankSnapshot.bankName || '';
            this.form.accountName = row.bankSnapshot.accountName || '';
            this.form.iban = row.bankSnapshot.iban || '';
            this.form.accountNumber = row.bankSnapshot.accountNumber || '';
          }
        } else if (this.branches.length === 1) {
          this.form.branchId = this.branches[0].id;
        }
        if (!offerResponse && this.bankAccounts.length === 1) {
          this.form.bankAccountId = this.bankAccounts[0].id;
          this.chooseBankAccount();
        }
      } catch (error) {
        this.requestError(error);
      } finally {
        this.loading = false;
      }
    },
    async submitOffer() {
      if (this.saving) return;
      if (!this.form.branchId || !this.form.employeeName || !this.form.clientName ||
          !this.form.clientMobile || !this.form.propertyName || !this.form.unitNumber ||
          (!this.isEdit && !this.form.deedNumber) || !this.expiryDate) {
        return this.fail('يرجى تعبئة الحقول المطلوبة');
      }
      if (Number(this.form.areaSqm) <= 0) return this.fail('المساحة يجب أن تكون أكبر من صفر');
      if (!this.form.manualBasePrice && Number(this.form.pricePerSqm) <= 0)
        return this.fail('سعر المتر يجب أن يكون أكبر من صفر');
      if (Number(this.form.basePrice) <= 0) return this.fail('السعر الأساسي يجب أن يكون أكبر من صفر');
      if (this.discountInvalid) return this.fail('مبلغ الخصم يجب ألا يتجاوز السعر الأساسي');
      this.recalculateInstallments();
      if (this.form.installments.length > 24) return this.fail('الحد الأقصى 24 دفعة');
      if (this.form.installments.some(row => !row.label || !row.dueCondition || toCents(row.amount) <= 0))
        return this.fail('أكمل بيانات الدفعات المالية أو احذف الصف غير المكتمل');
      if (toCents(this.installmentsTotal) > toCents(this.totals.totalAmount))
        return this.fail('إجمالي الدفعات لا يمكن أن يتجاوز إجمالي العرض النهائي');
      if (!this.form.bankName || !this.form.accountName || !this.form.iban || !this.form.accountNumber || !this.form.paymentNote)
        return this.fail('أكمل بيانات الحساب البنكي');
      if (!/^[A-Z]{2}[A-Z0-9]{13,32}$/.test(this.form.iban.replace(/\s/g, '').toUpperCase()))
        return this.fail('رقم الآيبان غير صحيح');
      if (!/^[+\d\s()-]{5,30}$/.test(this.form.clientMobile))
        return this.fail('رقم الجوال غير صحيح');
      const fields = [
        'branchId', 'employeeName', 'offerDate', 'durationDays', 'gracePeriodDays', 'purpose',
        'clientName', 'clientMobile', 'clientEmail', 'propertyName', 'unitNumber',
        'deedNumber', 'propertyDescription', 'areaSqm', 'pricePerSqm', 'manualBasePrice',
        'basePrice', 'discountAmount', 'commissionMethod', 'commissionRate',
        'waterFee', 'contractFee', 'insuranceFee', 'bankAccountId',
        'bankName', 'accountName', 'iban', 'accountNumber', 'paymentNote',
      ];
      const payload = Object.fromEntries(fields.map(field => [field, this.form[field]]));
      payload.installments = this.form.installments.map(row => ({
        label: row.label.trim(),
        percentage: submittedPercentageFor(toCents(row.amount), toCents(this.totals.totalAmount)),
        amount: Number(row.amount), dueCondition: row.dueCondition.trim(),
      }));
      payload.iban = payload.iban.replace(/\s/g, '').toUpperCase();
      payload.bankAccountId = payload.bankAccountId || null;
      for (const field of ['areaSqm', 'pricePerSqm', 'basePrice', 'discountAmount', 'commissionRate', 'waterFee', 'contractFee', 'insuranceFee']) {
        payload[field] = Number(payload[field]) || 0;
      }
      payload.durationDays = Number(payload.durationDays);
      payload.gracePeriodDays = Number(payload.gracePeriodDays) || 0;
      this.saving = true;
      this.hasError = false;
      try {
        if (this.isEdit) {
          await this.$http.put(`/api/admin/pricesOffers/update/${this.$route.params.id}`, payload);
        } else {
          await this.$http.post('/api/admin/pricesOffers/create', payload);
        }
        await this.$router.push('/pricesOffers');
      } catch (error) {
        this.requestError(error);
      } finally {
        this.saving = false;
      }
    },
  },
  created() { this.load(); },
};
</script>

<style scoped>
.prices-offer-form {
  --offer-header-bg: var(--brand-green-dark);
  --offer-border: rgba(62, 87, 46, .16);
  --offer-heading: var(--brand-green-dark);
  --offer-accent: var(--brand-green);
  --offer-box-bg: #f4f7f0;
  --offer-bank-bg: #f8f5ea;
  --offer-bank-border: #d8ca9f;
  --offer-calculated-bg: #ebf4df;
  --offer-payments-bg: #f4f8f2;
  --offer-payments-head: #e7efe2;
  --offer-payments-muted: #61715b;
  --offer-summary-divider: rgba(216, 202, 159, .28);
  --offer-summary-muted: #e4ebdc;
  --offer-summary-emphasis: #d8ca9f;
  --offer-required: #b85c4a;
}
.offer-card { border: 1px solid var(--offer-border); border-radius: 12px; overflow: hidden; }
.offer-card-header { background: var(--offer-header-bg); color: #fff; padding: 14px 20px; }
.offer-section { margin: 0 0 26px; padding: 0 0 22px; border-bottom: 1px solid var(--offer-border); }
.offer-section:last-child { border-bottom: 0; margin-bottom: 0; padding-bottom: 0; }
.offer-section h5 { font-size: 1rem; font-weight: 700; margin-bottom: 18px; color: var(--offer-heading); }
.section-number { color: var(--offer-accent); margin-left: 5px; }
.offer-section-box { background: var(--offer-box-bg); padding: 18px; border: 1px solid var(--offer-border); border-radius: 10px; }
.offer-section-bank { background: var(--offer-bank-bg); padding: 18px; border: 1px solid var(--offer-bank-border); border-radius: 10px; }
.offer-section-payments { background: var(--offer-payments-bg); padding: 18px; border: 1px solid var(--offer-border); border-radius: 10px; }
.calculated-value { background: var(--offer-calculated-bg); color: var(--offer-heading); font-weight: 700; }
.installment-table-wrap { border: 1px solid var(--offer-border); border-radius: 8px; }
.installment-table { --bs-table-bg: transparent; --bs-table-color: var(--offer-heading); --bs-table-border-color: var(--offer-border); min-width: 760px; }
.installment-table th { background: var(--offer-payments-head); color: var(--offer-heading); font-size: .85rem; white-space: nowrap; }
.installment-table td { min-width: 135px; }
.installment-table td:first-child,
.installment-table td:nth-child(4) { min-width: 190px; }
.installment-table td:last-child { min-width: 70px; }
.installment-empty { padding: 14px; border: 1px dashed var(--offer-border); border-radius: 8px; color: var(--offer-payments-muted); text-align: center; }
.installment-summary { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 12px; color: var(--offer-heading); font-size: .9rem; }
.offer-section-payments .form-text { color: var(--offer-payments-muted); }
.prices-offer-form .offer-section-payments .btn-outline-primary { color: var(--offer-accent); border-color: var(--offer-accent); }
.prices-offer-form .offer-section-payments .btn-outline-primary:hover,
.prices-offer-form .offer-section-payments .btn-outline-primary:focus { color: #fff; background: var(--offer-accent); }
.offer-summary { display: grid; grid-template-columns: repeat(4, 1fr); background: var(--offer-header-bg); color: #fff; border-radius: 8px; overflow: hidden; }
.offer-summary > div { display: flex; flex-direction: column; gap: 5px; padding: 14px; border-left: 1px solid var(--offer-summary-divider); }
.offer-summary > div:last-child { border-left: 0; }
.offer-summary small { color: var(--offer-summary-muted); }
.offer-summary strong { font-size: 1.15rem; }
.offer-summary > div:first-child strong { color: var(--offer-summary-emphasis); }
.prices-offer-form .btn-outline-secondary { color: var(--brand-green); border-color: rgba(62, 87, 46, .36); }
.prices-offer-form .btn-outline-secondary:hover,
.prices-offer-form .btn-outline-secondary:focus { color: #fff; background: var(--brand-green); border-color: var(--brand-green); }
.req { color: var(--offer-required); }
@media (max-width: 767px) { .offer-summary { grid-template-columns: 1fr; } .offer-summary > div { border-left: 0; border-bottom: 1px solid var(--offer-summary-divider); } }
</style>

<style>
html[data-bs-theme="dark"] .prices-offer-form {
  --offer-header-bg: #263a20;
  --offer-border: rgba(166, 144, 87, .24);
  --offer-heading: #dfe9d9;
  --offer-accent: #b7cdaa;
  --offer-box-bg: #202a1e;
  --offer-bank-bg: #30291d;
  --offer-bank-border: #74613b;
  --offer-calculated-bg: #293526;
  --offer-payments-bg: #202a1e;
  --offer-payments-head: #2c3928;
  --offer-payments-muted: #bdcbb5;
  --offer-summary-divider: rgba(216, 202, 159, .28);
  --offer-summary-muted: #c3d0bc;
  --offer-summary-emphasis: #d8ca9f;
  --offer-required: #f0a098;
}
html[data-bs-theme="dark"] .prices-offer-form .offer-card-header {
  background: var(--offer-header-bg);
  color: #edf1e9;
}
html[data-bs-theme="dark"] .prices-offer-form .offer-section-box,
html[data-bs-theme="dark"] .prices-offer-form .offer-section-bank,
html[data-bs-theme="dark"] .prices-offer-form .offer-section-payments {
  color: #e4eae0;
}
html[data-bs-theme="dark"] .prices-offer-form .calculated-value {
  background: var(--offer-calculated-bg);
  border-color: var(--offer-border);
  color: #e4eae0;
}
html[data-bs-theme="dark"] .prices-offer-form .offer-summary { color: #edf1e9; }
html[data-bs-theme="dark"] .prices-offer-form .installment-table { --bs-table-color: #e4eae0; }
html[data-bs-theme="dark"] .prices-offer-form .installment-table th { color: #e4eae0; }
html[data-bs-theme="dark"] .prices-offer-form .installment-summary { color: #e4eae0; }
html[data-bs-theme="dark"] .prices-offer-form .btn-outline-secondary { color: #c5d8b8; border-color: #52634b; }
html[data-bs-theme="dark"] .prices-offer-form .btn-outline-secondary:hover,
html[data-bs-theme="dark"] .prices-offer-form .btn-outline-secondary:focus { color: #fff; background: #3e572e; border-color: #6f8a5a; }
</style>
